import type { FC } from 'react';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { createTipJarTier } from '@wix/da-shared-react/pkg/Tier/redux/creator/actions';
import { popModal, pushModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import TierReduxModule from '@wix/da-shared-react/pkg/Tier/redux/creator/ModuleLoadable';
import s from './CreateTierCallToAction.scss';

interface Props {}

const CreateTierCallToAction: FC<Props> = () => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const dispatchCreateTipJarTier = useAction(createTipJarTier);
  const dispatchPushModal = useAction(pushModal);
  const dispatchPopModal = useAction(popModal);

  const handleCreateTipJarClick = useCallback(() => {
    dispatchPopModal();
    dispatchCreateTipJarTier();
  }, [dispatchPopModal, dispatchCreateTipJarTier]);

  const handleCreateTierClick = useCallback(() => {
    dispatchPopModal();
    dispatchPushModal(ModalType.TIER_EDIT, { wrapInModal: false });
  }, [dispatchPopModal, dispatchPushModal]);

  return (
    <div className={s['root']}>
      <TierReduxModule />
      {hasCoreMembership ? (
        <>
          <span>{t('studio.batchActions.form.edit_tier.cta.core.heading')}</span>
          <span>{t('studio.batchActions.form.edit_tier.cta.core.paragraph')}</span>
        </>
      ) : (
        <>
          <span>{t('studio.batchActions.form.edit_tier.cta.noCore.heading')}</span>
          <span>{t('studio.batchActions.form.edit_tier.cta.noCore.paragraph')}</span>
        </>
      )}

      <div className={s['buttons']}>
        {!hasCoreMembership && (
          <>
            <GhostButton size="large" variant="main" onClick={handleCreateTierClick}>
              {t('studio.batchActions.form.edit_tier.cta.create.label')}
            </GhostButton>
            <BrandedButton
              variant="core-orange"
              size="large"
              href={Url.coreMembershipLink({ referer: Url.studio() })}
              target="_blank"
            >
              {t('studio.batchActions.form.edit_tier.cta.upgrade.label')}
            </BrandedButton>
          </>
        )}
        {hasCoreMembership && (
          <>
            <GhostButton size="large" variant="main" onClick={handleCreateTipJarClick}>
              {t('studio.batchActions.form.edit_tier.cta.tipJar.label')}
            </GhostButton>
            <BrandedButton variant="brand-green" size="large" onClick={handleCreateTierClick}>
              {t('studio.batchActions.form.edit_tier.cta.create.label')}
            </BrandedButton>
          </>
        )}
      </div>
    </div>
  );
};

CreateTierCallToAction.displayName = 'CreateTierCallToAction';

export default CreateTierCallToAction;
